import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { useFontSize } from "src/hooks/useFontSize";

import Hint from "../NewComponents/Default/Hint";

type Level = "hard" | "medium" | "easy";

const levelMapper: Record<Level, string> = {
	hard: "wrong",
	medium: "warning",
	easy: "correct",
};

interface LevelChipProps {
	level: Level;
}

export const LevelChip = ({ level }: LevelChipProps) => {
	const { getFontSizeClass } = useFontSize();
	const { t } = useTranslation();

	return (
		<div
			className={`w-fit-content answer--${levelMapper[level]} px-4 py-2 border-radius-4 flex-center-row`}
		>
			<span className={getFontSizeClass(-2)}>
				{t(`filters.exams.level.levels.${level}`)}
			</span>
		</div>
	);
};

interface HintInfoProps {
	className?: string;
	percentOfWrongAnswers: number;
	percentOfCorrectAnswers: number;
}

const HintInfo = styled(
	({ className, percentOfWrongAnswers, percentOfCorrectAnswers }: HintInfoProps) => {
		const { t } = useTranslation();

		return (
			<div className={className}>
				<p>{t("filters.exams.level.hint.title")}:</p>
				<span>
					{t("filters.exams.level.hint.correctAnswers")}:{" "}
					<label>{percentOfWrongAnswers}%</label>
				</span>
				<span>
					{t("filters.exams.level.hint.wrongAnswers")}:{" "}
					<label>{percentOfCorrectAnswers}%</label>
				</span>
			</div>
		);
	},
)`
  display: flex;
  flex-direction: column;
  font-size: 12px;

  p {
    font-weight: 600;
    margin: 4px 0;
  }

  > span {
    > label {
      font-weight: bold;
    }
  }
`;

interface QuestionLevelChipDetails {
	level: Level;
	percentOfWrongAnswers: number;
	percentOfCorrectAnswers: number;
}

interface QuestionLevelChipProps {
	className?: string;
	details: QuestionLevelChipDetails;
}

function QuestionLevelChip({ className, details }: QuestionLevelChipProps) {
	const { level, percentOfWrongAnswers, percentOfCorrectAnswers } = details;

	return (
		<Hint
			className={className}
			link={undefined}
			Trigger={<LevelChip level={level} />}
			hint={
				<HintInfo
					percentOfWrongAnswers={percentOfWrongAnswers}
					percentOfCorrectAnswers={percentOfCorrectAnswers}
				/>
			}
		/>
	);
}

export default styled(QuestionLevelChip)`
  cursor: pointer;
  line-height: 1;
`;
